import styles from "./addjournal.module.css";
import { Autocomplete, Button, createFilterOptions, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Camera, Video, X } from "react-feather";
import { Editor } from "@tinymce/tinymce-react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useRef } from "react";
import { toast } from 'react-smart-toaster';
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { useState, useEffect } from "react";
import VideoThumbnail from "./VideoThumbnail";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const schema = yup.object().shape({
    title: yup.string().required("This field is Required"),
    tenants: yup.string(),
    property_id: yup.string().required("This field is Required"),
    is_unusual: yup.string().required("This field is Required"),
    description: yup.string().required("This field is Required")
});

const filterOptions = createFilterOptions({
    matchFrom: 'start'
});

const AddJournal = ({ journalData, propertyList, allTenantList, setOpenAddForm, setLoading, setSnackbarMsg, setSnackbarOpen, onSubmitLocal }) => {
    const editorRef = useRef(null);
    const { getAccessTokenSilently } = useAuth0();
    const [prevImages, setPrevImages] = useState([]);
    const [prevVideos, setPrevVideos] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [uploadedVideos, setUploadedVideos] = useState([]);
    const [displayValue, setDisplayValue] = useState(`Today: ${dayjs().format("MM/DD/YYYY")}`);
    const [selectedProperty, setSelectedProperty] = useState(0);
    const [tenantList, setTenantList] = useState([]);

    const { handleSubmit, control, errors, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: "",
            is_unusual: "",
            description: "",
            date: dayjs(),
            property_id: "",
            tenants: [],
        }
    });

    useEffect(() => {
        if (selectedProperty > 0) {
            let allTenantListTemp = allTenantList?.map(i => {
                if (i?.group_id === selectedProperty)
                    return i;
                return null;
            });
            allTenantListTemp = allTenantListTemp?.filter(i => i !== null);
            setTenantList(allTenantListTemp);
        } else {
            setTenantList([]);
        }
    }, [allTenantList, selectedProperty])

    useEffect(() => {
        if (journalData) {
            const videoExtensions = ["mp4", "mov", "avi", "mkv", "webm"];

            setValue('title', journalData?.title?.trim() || "Journal Entry");
            setValue('is_unusual', journalData?.is_unusual);
            setValue('description', journalData?.description);
            setValue('date', dayjs(journalData?.date));

            if (dayjs(journalData?.date).isSame(dayjs(), "day")) {
                setDisplayValue(`Today: ${dayjs().format("MM/DD/YYYY")}`);
            } else {
                setDisplayValue(dayjs(journalData?.date).format("MM/DD/YYYY"));
            }

            if (parseInt(journalData?.property_id) > 0) {
                setValue('property_id', journalData?.property_id);
                setSelectedProperty(parseInt(journalData?.property_id));
            }
            setValue('tenants', journalData?.tenants.split(",").map(Number));

            let imagesTemp = journalData?.images?.map(i => {
                const extension = i?.image_url.split(".").pop().split("?")[0].toLowerCase();
                if (!videoExtensions.includes(extension)) {
                    return i;
                }
                return null;
            });

            imagesTemp = imagesTemp?.filter(i => i !== null);

            let videosTemp = journalData?.images?.map(i => {
                const extension = i?.image_url.split(".").pop().split("?")[0].toLowerCase();
                if (videoExtensions.includes(extension)) {
                    return i;
                }
                return null;
            });

            videosTemp = videosTemp?.filter(i => i !== null);

            if (imagesTemp?.length > 0) {
                setPrevImages(imagesTemp);
            }

            if (videosTemp?.length > 0) {
                setPrevVideos(videosTemp);
            }
        }
    }, [journalData, setValue])

    const onSubmit = async (data) => {
        setOpenAddForm(false);
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        let tenantsVal = "";
        if (Array.isArray(data?.tenants)) {
            tenantsVal = data.tenants.join(",");
        } else if (typeof data?.tenants === "string") {
            tenantsVal = data.tenants;
        }

        let postData = {
            ...data,
            tenants: tenantsVal
        };

        let images = uploadedImages?.map(i => {
            return i?.file_path;
        });

        let videos = uploadedVideos?.map(i => {
            return i?.file_path;
        });

        postData = { ...postData, images: images.concat(videos), date: dayjs(data?.date).format("YYYY-MM-DD") };

        axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/journals", postData, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then((response) => {
            setLoading(false);
            setSnackbarMsg(response.data.message);
            setSnackbarOpen(true);

            onSubmitLocal(response.data.data);
        }).catch((error) => {
            if (typeof error.response !== "undefined")
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const onUpdate = async (data) => {
        setOpenAddForm(false);
        setLoading(true);
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        let tenantsVal = "";
        if (Array.isArray(data?.tenants)) {
            tenantsVal = data.tenants.join(",");
        } else if (typeof data?.tenants === "string") {
            tenantsVal = data.tenants;
        }

        let postData = {
            ...data,
            title: data?.title?.trim() || "Journal Entry",
            tenants: tenantsVal
        };

        let images = uploadedImages?.map(i => {
            return i?.file_path;
        });

        let videos = uploadedVideos?.map(i => {
            return i?.file_path;
        });

        postData = { ...postData, images: images.concat(videos), date: dayjs(data?.date).format("YYYY-MM-DD") };

        axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/journals/" + journalData?.id, postData, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then((response) => {
            setLoading(false);
            setSnackbarMsg(response.data.message);
            setSnackbarOpen(true);

            onSubmitLocal(response.data.data);
        }).catch((error) => {
            if (typeof error.response !== "undefined")
                toast.error(error.response.data.message);
            setLoading(false);
        });
    }

    const handleFileChange = async (e) => {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        const allowedExtensions = ["jpg", "jpeg", "png"];
        const maxImageSize = 5 * 1024 * 1024; // 5MB

        for (let n = 0; n < e.target.files.length; n++) {
            let file = e.target.files[n];
            const extension = file.name.split(".").pop().toLowerCase();

            if (!allowedExtensions.includes(extension)) {
                toast.error(`Invalid image format: ${file.name}. Only JPG, JPEG, PNG formats are supported.`);
                continue;
            }

            if (file.size > maxImageSize) {
                toast.error(`Image size too large: ${file.name}. Maximum allowed size is 5MB.`);
                continue;
            }

            let formData = new FormData();
            formData.append("file", file);

            setLoading(true);

            axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/upload-journal-image", formData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }).then((response) => {
                setLoading(false);

                setUploadedImages(prev => {
                    return prev.concat([response.data.data]);
                })
            }).catch((error) => {
                if (typeof error.response !== "undefined")
                    toast.error(error.response.data.message);
                setLoading(false);
            });
        }
    }

    const handleFileChange2 = async (e) => {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        const allowedExtensions = ["mp4", "mov", "webm", "avi", "mkv"];
        const maxVideoSize = 10 * 1024 * 1024; // 10MB

        for (let n = 0; n < e.target.files.length; n++) {
            let file = e.target.files[n];
            const extension = file.name.split(".").pop().toLowerCase();

            if (!allowedExtensions.includes(extension)) {
                toast.error(`Invalid video format: ${file.name}. Only MP4, MOV, WEBM, AVI, and MKV formats are supported.`);
                continue;
            }

            if (file.size > maxVideoSize) {
                toast.error(`Video size too large: ${file.name}. Maximum allowed size is 10MB.`);
                continue;
            }

            let formData = new FormData();
            formData.append("file", file);

            setLoading(true);

            axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/upload-journal-video", formData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }).then((response) => {
                setLoading(false);

                setUploadedVideos(prev => {
                    return prev.concat([response.data.data]);
                })
            }).catch((error) => {
                if (typeof error.response !== "undefined")
                    toast.error(error.response.data.message);
                setLoading(false);
            });
        }
    }

    const deleteImage = async (file_path) => {
        setPrevImages(prev => {
            return prev.filter(i => i?.image_path !== file_path);
        });

        setUploadedImages(prev => {
            return prev.filter(i => i?.file_path !== file_path);
        });

        setPrevVideos(prev => {
            return prev.filter(i => i?.image_path !== file_path);
        });

        setUploadedVideos(prev => {
            return prev.filter(i => i?.file_path !== file_path);
        });

        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        axios.delete(process.env.REACT_APP_TENANT_API_URL + "v1/delete-journal-image/" + file_path, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }).then((response) => {
            setLoading(false);
        }).catch((error) => {
            setLoading(false);
        });
    }

    return (<div>
        <form className={`${styles.JournalForm}`} onSubmit={handleSubmit(journalData ? onUpdate : onSubmit)}>
            <div className={`${styles.ReqAddRow}`}>
                <div className={`${styles.ReqAddFormSec}`}>
                    <div className={`${styles.FormGroupFull} FormGroup`}>
                        <label className={`${styles.FilterLabel}`}>Journal Title*</label>
                        <FormControl fullWidth>
                            <Controller
                                name="title"
                                control={control}
                                render={(field) => (
                                    <TextField
                                        {...field}
                                        label="Enter Title"
                                        variant="outlined"
                                        fullWidth
                                        spellCheck={true}
                                        inputProps={{
                                            spellCheck: true,
                                            autoCorrect: "on",
                                            autoCapitalize: "sentences",
                                        }}
                                    />
                                )}
                            />
                            {errors?.title && (<p className={`${styles.ErrorM}`}>{errors?.title?.message}</p>)}
                        </FormControl>
                    </div>
                    <div className={`${styles.FormGroupFull} FormGroup`}>
                        <label className={`${styles.FilterLabel}`}>Date</label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Controller
                                name="date"
                                control={control}
                                render={(field) => (<DatePicker
                                    {...field}
                                    onChange={(e) => {
                                        field.onChange(e);

                                        if (e.isSame(dayjs(), "day")) {
                                            setDisplayValue(`Today: ${dayjs().format("MM/DD/YYYY")}`);
                                        } else {
                                            setDisplayValue(e.format("MM/DD/YYYY"));
                                        }
                                    }}
                                    slots={{
                                        textField: (params) => (<TextField
                                            {...params}
                                            value={displayValue}
                                        />),
                                    }}
                                    disableFuture={true}
                                />)}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className={`${styles.FormHalfSecFull}`}>
                        <div className={`${styles.FormSec}`}>
                            <div className={`${styles.FormGroupFull} FormGroup`}>
                                <label className={`${styles.FilterLabel}`}>Property Selection*</label>
                                <FormControl fullWidth>
                                    <InputLabel id="demo-simple-select-label">Select Property</InputLabel>
                                    <Controller
                                        name="property_id"
                                        control={control}
                                        render={(field) => (<Select
                                            {...field}
                                            onChange={(e) => {
                                                field.onChange(e);

                                                setSelectedProperty(e?.target?.value);
                                                setValue('tenants', []);
                                            }}
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            label="Select Property"
                                        >
                                            {propertyList?.map(i => {
                                                return <MenuItem value={i?.id}>{i?.name}</MenuItem>
                                            })}
                                        </Select>)}
                                    />
                                    {errors?.property_id && (<p className={`${styles.ErrorM}`}>{errors?.property_id?.message}</p>)}
                                </FormControl>
                            </div>
                        </div>
                    </div>
                    <div className={`${styles.FormGroupFull} FormGroup MultiSelect`}>
                        <label className={`${styles.FilterLabel}`}>Tenant(s) involved</label>
                        <FormControl fullWidth>
                            <Controller
                                name="tenants"
                                control={control}
                                render={(field) => (<Autocomplete
                                    multiple
                                    filterOptions={filterOptions}
                                    options={selectedProperty > 0 ? tenantList : [{ label: "Select property first", value: "", disabled: true }]}
                                    //groupBy={(option) => option.group}
                                    getOptionLabel={(option) => option.label}
                                    getOptionDisabled={(option) => !!option.disabled}
                                    renderInput={(params) => (<TextField {...params} label="Select Tenant" />)}
                                    value={tenantList.filter((t) => (field?.value || []).includes(t.value))}
                                    onChange={(event, newValue) => {
                                        if (selectedProperty === 0) return;
                                        setValue('tenants', newValue?.map(i => i?.value));
                                    }}
                                />)}
                            />
                        </FormControl>
                    </div>
                    <div className={`${styles.FormHalfSecFull}`}>
                        <div className={`${styles.FormSec}`}>
                            <div className={`${styles.FormGroupFull} FormGroup`}>
                                <label className={`${styles.FilterLabel}`}>Security Observation*</label>
                                <FormControl fullWidth>
                                    <InputLabel id="demo-simple-select-label">Any unusual activities or events to report</InputLabel>
                                    <Controller
                                        name="is_unusual"
                                        control={control}
                                        render={(field) => (<Select
                                            {...field}
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            label="Any unusual activities or events to report"
                                        >
                                            <MenuItem value={1}>Yes</MenuItem>
                                            <MenuItem value={0}>No</MenuItem>
                                        </Select>)}
                                    />
                                    {errors?.is_unusual && (<p className={`${styles.ErrorM}`}>{errors?.is_unusual?.message}</p>)}
                                </FormControl>
                            </div>
                        </div>
                    </div>
                    <div className={`${styles.FormHalfSecFull}`}>
                        <div className={`${styles.FormSec}`}>
                            <div className={`${styles.FormGroupFull} FormGroup`}>
                                <label className={`${styles.FilterLabel}`}>Write a note*</label>
                                <Controller
                                    name="description"
                                    control={control}
                                    render={(field) => (
                                        <Editor
                                            onInit={(evt, editor) => { editorRef.current = editor }}
                                            className={`${styles.formControlTextArea}`}
                                            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
                                            value={field?.value}
                                            init={{
                                                height: 300,
                                                menubar: false,
                                                browser_spellcheck: true,
                                                placeholder: 'Add security shift notes…',
                                                toolbar:
                                                    "undo redo | bold italic underline strikethrough | fontselect fontsizeselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat |  fullscreen  | ltr rtl | code",
                                            }}
                                            onEditorChange={(e) => field.onChange(e)}
                                        />
                                    )}
                                />
                                {errors.description && (<p className={`${styles.ErrorM}`}>{errors.description.message}</p>)}
                            </div>
                        </div>
                    </div>
                    <div className={`${styles.FormIMGSec} ${styles.FileUploadSmall}`}>
                        <label className={`${styles.FilterLabel}`}>
                            Share a video or photo
                            <span style={{ display: 'block', fontSize: '11px', color: '#9a9aad', fontWeight: 'normal', marginTop: '4px' }}>
                                Images: JPG, JPEG, PNG (Max 5MB) · Videos: MP4, MOV, WEBM (Max 10MB)
                            </span>
                        </label>
                        <div className={`${styles.FileIMGDiv}`}>
                            <input id="file-upload" type="file" accept="image/*" capture="environment" onChange={handleFileChange} style={{ display: 'none' }} />
                            <input id="file-upload-2" type="file" accept="video/*" capture="environment" onChange={handleFileChange2} style={{ display: 'none' }} />
                            <div className={`${styles.UploadBUSec}`}>
                                <label className={`${styles.TakePhoteBU}`} for="file-upload" style={{ cursor: 'pointer' }}><Camera /> Take Photo</label>
                                <label className={`${styles.TakePhoteBU}`} for="file-upload-2" style={{ cursor: 'pointer' }}><Video /> Record Video</label>
                            </div>
                            <div className={`${styles.UpIMGSec}`}>
                                {prevImages?.map(i => {
                                    return (<div className={`${styles.ImgFile}`}>
                                        <img src={i?.image_url} alt="" />
                                        <button type="button" onClick={(e) => deleteImage(i?.image_path)}> <X /> </button>
                                    </div>)
                                })}

                                {uploadedImages?.map(i => {
                                    return (<div className={`${styles.ImgFile}`}>
                                        <img src={i?.url} alt="" />
                                        <button type="button" onClick={(e) => deleteImage(i?.file_path)}> <X /> </button>
                                    </div>)
                                })}

                                {prevVideos?.map(i => {
                                    return (<VideoThumbnail videoUrl={i?.image_url} file_path={i?.image_path} onRemove={deleteImage.bind(this)} />)
                                })}

                                {uploadedVideos?.map(i => {
                                    return (<VideoThumbnail videoUrl={i?.url} file_path={i?.file_path} onRemove={deleteImage.bind(this)} />)
                                })}
                                {/*uploadedImages?.length > 0 && <label className={`${styles.TakePhoteSQRBU}`} for="file-upload" style={{ cursor: 'pointer' }}><Plus /></label>*/}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${styles.ButtonAction}`}>
                <Button className={`${styles.SubmitPopupBU}`} type="submit">{journalData ? 'Update' : 'Submit'}</Button>
            </div>
        </form>
    </div>);
};

export default AddJournal;